/**
 * Local player diagnostics — works in any plain browser.
 *
 * Enable:
 *   /player?player_debug=1
 *   localStorage.setItem("omnimedia:player-debug", "1")
 *
 * Logs go to the browser console and (when realtime is connected) device_events
 * as type `player.debug` visible in Fleet → Diagnostics → Recent activity.
 *
 * DEVLOGS-TEMP: every category is ALSO fanned into the dev debug-log pipeline
 * (docs/dev-log-pipeline.md), regardless of whether the on-screen panel is
 * enabled. That path is inert unless VITE_DEVLOG_URL is baked into the build.
 */

import { devlog } from "@workspace/devlog";

export type PlayerDebugEntry = {
  ts: string;
  category: string;
  data: Record<string, unknown>;
};

const STORAGE_KEY = "omnimedia:player-debug";
const MAX_BUFFER = 120;

const buffer: PlayerDebugEntry[] = [];

let enabled: boolean | null = null;

export function isPlayerDebugEnabled(): boolean {
  if (enabled !== null) return enabled;
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("player_debug") === "1") {
      sessionStorage.setItem(STORAGE_KEY, "1");
    }
    enabled =
      sessionStorage.getItem(STORAGE_KEY) === "1" ||
      localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    enabled = false;
  }
  return enabled;
}

export function setPlayerDebugEnabled(on: boolean): void {
  enabled = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "1");
    else {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function getPlayerDebugBuffer(): readonly PlayerDebugEntry[] {
  return buffer;
}

export type AudioElementSnapshot = {
  paused: boolean;
  muted: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  readyState: number;
  networkState: number;
  errorCode: number | null;
  srcHost: string | null;
  ended: boolean;
};

export function snapshotAudioElement(
  el: HTMLAudioElement | null | undefined,
): AudioElementSnapshot | null {
  if (!el) return null;
  let srcHost: string | null = null;
  try {
    if (el.src) srcHost = new URL(el.src).host;
  } catch {
    srcHost = el.src ? "(invalid-url)" : null;
  }
  return {
    paused: el.paused,
    muted: el.muted,
    volume: el.volume,
    currentTime: el.currentTime,
    duration: Number.isFinite(el.duration) ? el.duration : 0,
    readyState: el.readyState,
    networkState: el.networkState,
    errorCode: el.error?.code ?? null,
    srcHost,
    ended: el.ended,
  };
}

export function playerDebugLog(
  category: string,
  data: Record<string, unknown> = {},
  sendMetric?: (metric: string, payload?: Record<string, unknown>) => void,
): void {
  const entry: PlayerDebugEntry = {
    ts: new Date().toISOString(),
    category,
    data,
  };
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.shift();

  // DEVLOGS-TEMP: fan every category into the dev debug-log pipeline. Placed
  // BEFORE the enabled-guard on purpose, so devlog receives everything whether
  // or not the on-screen panel is toggled on — and, critically, the panel's own
  // visibility stays gated by isPlayerDebugEnabled() ALONE. Four e2e specs
  // assert the panel is absent without `?player_debug=1`; tying it to devlog
  // instead would break them. Inert (and silent) when devlog is unconfigured.
  devlog.debug("web.player", category, data);

  if (!isPlayerDebugEnabled()) return;

  console.info(`[player:${category}]`, data);
  sendMetric?.("player.debug", { category, ...data });
}
